import { useState } from "react";
import { Bell, Users, Mail, MessageCircle, ChevronRight, SendIcon } from "lucide-react";

import { CustomOption, CustomSingleValue } from "@/components/desktop/list/table/cells/SelectCell/CustomSelectComponents";
import CustomModalForm from "@/components/desktop/form/modal/CustomModalForm/CustomModalForm";
import CustomInputSelect from "@/components/desktop/form/input/input-select/CustomInputSelect";

import styles from "./oa-notice-select.module.scss";

const OANoticeSelect = ({ show, onHide, onNext, distributionListsData }) => {
	const [selectedLists, setSelectedLists] = useState([]);
	const [notificationMethods, setNotificationMethods] = useState({});

	const distributionLists = distributionListsData || [];

	const _notificationOptions = [
		{ value: "whatsapp", label: "WhatsApp" },
		{ value: "email", label: "Email" },
	];

	const handleToggle = (listId) => {
		setSelectedLists((prev) => {
			const newSelection = prev.includes(listId) ? prev.filter((id) => id !== listId) : [...prev, listId];

			if (!newSelection.includes(listId)) {
				const newMethods = { ...notificationMethods };
				delete newMethods[listId];
				setNotificationMethods(newMethods);
			}

			return newSelection;
		});
	};

	const handleNotificationMethodChange = (listId, selectedOption) => {
		setNotificationMethods((prev) => ({
			...prev,
			[listId]: selectedOption?.value || null,
		}));
	};

	const handleNext = () => {
		const listsWithMethods = selectedLists.map((listId) => ({
			listId,
			method: notificationMethods[listId],
		}));
		onNext(listsWithMethods);
	};

	const canProceed = () => {
		if (selectedLists.length === 0) return false;
		return selectedLists.every((listId) => notificationMethods[listId]);
	};

	const getSelectedListsText = () => {
		if (selectedLists.length === 0) return "";
		const selectedNames = distributionLists
			.filter((list) => selectedLists.includes(list.id))
			.map((list) => {
				const method = notificationMethods[list.id];
				const methodText = method === "whatsapp" ? "WhatsApp" : method === "email" ? "Email" : "";
				return `${list.name} (${methodText})`;
			})
			.join(", ");
		return `Se enviará notificación a ${selectedLists.length} grupo(s): ${selectedNames}`;
	};

	const getAvailableOptions = (list) => {
		const available = [];
		const types = list.notification_type?.split(",").map((type) => type.trim()) || [];

		if (types.includes("whatsapp")) {
			available.push({ value: "whatsapp", label: "WhatsApp" });
		}
		if (types.includes("email")) {
			available.push({ value: "email", label: "Email" });
		}

		return available;
	};

	const _formatOptionLabel = (option) => {
		const icon = option.value === "whatsapp" ? <MessageCircle size={14} /> : <Mail size={14} />;
		return (
			<div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
				{icon}
				<span>{option.label}</span>
			</div>
		);
	};

	return (
		<CustomModalForm
			open={show}
			onClose={onHide}
			title={
				<span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
					<Bell size={20} />
					Dar Aviso de Aplicación
				</span>
			}
			size="md"
			showSave={false}
			footer={
				<div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
					<button className={styles.btnCancel} onClick={onHide}>
						Cancelar
					</button>
					<button className={styles.btnNext} onClick={handleNext} disabled={!canProceed()}>
						<SendIcon size={16} />
						Enviar Notificación
						<ChevronRight size={16} />
					</button>
				</div>
			}
		>
			<p className={styles.description}>Seleccione las listas de difusión a las que desea enviar el aviso de aplicación y el método de notificación.</p>
			<p className={styles.subtitle}>Se enviará aviso de 1 orden seleccionada.</p>

			<div className={styles.listContainer}>
				{distributionLists.map((list) => {
					const isSelected = selectedLists.includes(list.id);
					const availableOptions = getAvailableOptions(list);

					return (
						<div key={list.id} className={`${styles.listItem} ${isSelected ? styles.selected : ""}`}>
							<div className={styles.listContent}>
								<div className={styles.checkboxWrapper} onClick={() => handleToggle(list.id)}>
									<input type="checkbox" checked={isSelected} onChange={() => {}} />
								</div>
								<div className={styles.listInfo} onClick={() => handleToggle(list.id)}>
									<span className={styles.listName}>{list.name}</span>
									<span className={styles.contactCount}>
										<Users size={13} />
										{list.contact_count || 0} contacto(s)
									</span>
								</div>
								{isSelected && (
									<div className={styles.selectorWrapper}>
										<label className={styles.selectorLabel}>Notificar por:</label>

										<CustomInputSelect
											options={availableOptions}
											value={notificationMethods[list.id] || null}
											onChange={(value) => handleNotificationMethodChange(list.id, { value })}
											placeholder="Seleccione método"
											creatable={true}
											isClearable={false}
											size="sm"
											components={{
												Option: CustomOption,
												SingleValue: CustomSingleValue,
											}}
										/>
									</div>
								)}
							</div>
						</div>
					);
				})}
			</div>

			{selectedLists.length > 0 && canProceed() && (
				<div className={styles.summary}>
					<div className={styles.summaryTitle}>Resumen:</div>
					<div className={styles.summaryText}>{getSelectedListsText()}</div>
				</div>
			)}

			{selectedLists.length > 0 && !canProceed() && (
				<div className={styles.warning}>
					<span>⚠️ Seleccione el método de notificación para todas las listas</span>
				</div>
			)}
		</CustomModalForm>
	);
};

export default OANoticeSelect;
